import { useState } from "react";
import { Plug, Loader2, KeyRound, Instagram, Info, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import { conectarConta } from "../services/instagramApi";

const TOKEN_REGEX = /^EAA[A-Za-z0-9]+$/;

const ESCOPOS = [
  "instagram_basic",
  "pages_show_list",
  "instagram_manage_comments",
  "instagram_manage_messages",
  "pages_manage_metadata",
  "pages_messaging",
];

export default function ConnectForm({ onSuccess }) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [pages, setPages] = useState(null); // lista de páginas quando MULTIPLE_PAGES
  const [selectedPage, setSelectedPage] = useState(null);

  function resolverErro(body) {
    switch (body?.error) {
      case "TOKEN_INVALID":
        return "Token inválido ou sem as permissões de Instagram.";
      case "NO_IG_ACCOUNT":
        return "Nenhuma conta Instagram Business vinculada às suas Páginas. Verifique no Business Manager.";
      case "INVALID_TOKEN_FORMAT":
        return "Token inválido. O System User Token deve começar com EAA.";
      case "INVALID_PAGE_ID":
        return "Página inválida. Selecione uma das páginas listadas.";
      case "RATE_LIMIT_EXCEEDED":
        return `Muitas tentativas, aguarde ${body.retry_after_sec ?? "alguns"} segundos.`;
      default:
        return body?.message || "Erro desconhecido ao conectar.";
    }
  }

  async function conectar(pageId) {
    setLoading(true);
    setErrorMsg(null);
    try {
      const conta = await conectarConta({
        system_user_token: token.trim(),
        page_id: pageId,
      });
      toast.success(`Instagram conectado: @${conta.username}`);
      onSuccess?.(conta);
    } catch (err) {
      const body = err.response?.data;
      if (body?.error === "MULTIPLE_PAGES" && Array.isArray(body.pages)) {
        setPages(body.pages);
        setSelectedPage(body.pages[0]?.page_id ?? null);
        setErrorMsg(null);
      } else {
        setPages(null);
        setErrorMsg(resolverErro(body) || (err.message ?? "Erro desconhecido"));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErrorMsg(null);
    setPages(null);
    if (!TOKEN_REGEX.test(token.trim())) {
      setErrorMsg("Token inválido. O System User Token deve começar com EAA.");
      return;
    }
    await conectar();
  }

  return (
    <form onSubmit={handleSubmit} className="surface-card p-6 max-w-2xl animate-slide-up">
      <h2 className="heading-3 text-ink-1 mb-1">Conectar Instagram</h2>
      <p className="text-sm text-ink-3 mb-6">
        Cole o System User Token permanente gerado no Business Manager para conectar sua conta
        Instagram Business à Cloud API da Meta.
      </p>

      {/* Help box */}
      <div className="rounded-xl border border-white/[0.06] bg-surface-3/40 p-4 mb-6 text-sm text-ink-2 space-y-3">
        <p className="flex items-center gap-2 font-medium text-ink-1">
          <Info className="h-4 w-4 text-brand-400" /> O que você precisa
        </p>
        <p className="text-ink-3">
          Um <strong className="text-ink-2">System User Token permanente</strong> do Business Manager
          (expiração <strong className="text-ink-2">Nunca</strong>) com os escopos:
        </p>
        <div className="flex flex-wrap gap-1.5">
          {ESCOPOS.map((s) => (
            <span key={s} className="chip-neutral !text-[11px] font-mono">
              {s}
            </span>
          ))}
        </div>
        <p className="text-ink-3">
          A conta do Instagram precisa ser <strong className="text-ink-2">Business ou Creator</strong>{" "}
          e estar vinculada a uma Página do Facebook.
        </p>
      </div>

      {/* Token */}
      <div className="mb-4">
        <label className="flex items-center gap-2 text-sm text-ink-2 mb-1.5">
          <KeyRound className="h-4 w-4" /> System User Token
        </label>
        <textarea
          value={token}
          onChange={(e) => {
            setToken(e.target.value);
            setPages(null);
            setErrorMsg(null);
          }}
          placeholder="EAA..."
          rows={3}
          spellCheck={false}
          className="input-field font-mono text-sm resize-none"
          required
        />
      </div>

      {/* Seleção de página quando MULTIPLE_PAGES */}
      {pages && (
        <div className="rounded-xl border border-white/[0.06] bg-surface-3/40 p-4 mb-4 space-y-3">
          <p className="text-sm text-ink-2">
            Você tem várias Páginas com Instagram. Escolha qual conta conectar:
          </p>
          <div className="flex flex-col gap-2">
            {pages.map((pg) => (
              <label
                key={pg.page_id}
                className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                  selectedPage === pg.page_id
                    ? "border-brand-500/60 bg-brand-500/10"
                    : "border-white/[0.08] hover:bg-white/[0.03]"
                }`}
              >
                <input
                  type="radio"
                  name="page"
                  value={pg.page_id}
                  checked={selectedPage === pg.page_id}
                  onChange={() => setSelectedPage(pg.page_id)}
                  className="accent-brand-500"
                />
                <div className="min-w-0">
                  <div className="text-sm text-ink-1 truncate">{pg.name}</div>
                  <div className="text-xs text-ink-4 font-mono truncate">@{pg.ig_user_id}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Erro */}
      {errorMsg && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-danger-500/30 bg-danger-500/10 p-3 text-sm text-danger-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{errorMsg}</span>
        </div>
      )}

      {pages ? (
        <button
          type="button"
          disabled={loading || !selectedPage}
          onClick={() => conectar(selectedPage)}
          className="btn-primary"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Instagram className="h-4 w-4" />
          )}
          <span>{loading ? "Conectando..." : "Conectar página selecionada"}</span>
        </button>
      ) : (
        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plug className="h-4 w-4" />
          )}
          <span>{loading ? "Conectando..." : "Conectar Instagram"}</span>
        </button>
      )}
    </form>
  );
}
